import React, { useEffect, useRef, useState } from "react";
import { doc, setDoc, addDoc, collection, serverTimestamp, runTransaction, Timestamp, arrayUnion } from "firebase/firestore";
import { db, auth } from "../firebase";
import { colors, radius, spacing, typography, btnSecondary } from "../utils/designTokens";
import { STATUS_LABELS } from "../utils/statusColors";
import AdminModal from "./AdminModal";
import FormField from "./FormField";
import AppSelect from "./AppSelect";
import { useToast } from "./Toast";
import { useAppDialog } from "./AppDialog";
import { FaBuilding, FaExternalLinkAlt, FaRegStickyNote, FaCheck, FaArrowLeft, FaLock, FaHandPointer, FaStar, FaPaperPlane, FaFileInvoiceDollar } from "react-icons/fa";
import ContactoAdministracion from "./ContactoAdministracion";
import { useIsMobile } from "../hooks/useResponsive";
import { useUser } from "../context/UserContext";
import { useCotizadorPrefill } from "../context/CotizadorContext";
import {
  computeBloqueadoHasta, estaBloqueado, estaLibre, esTitular, puedeEditar,
  esGestion, fmtFechaLiberacion,
} from "../utils/crmAsignacion";
import { PLANES_BO, IVA_LEYENDA, fmtU, fechaCortaBO } from "../utils/planesBackoffice";

// Mismo pipeline que el CRM de padrones / desarrolladoras.
export const CRM_ESTADOS = [
  "sin_contactar", "contactado", "en_conversacion", "reunion", "propuesta", "ganado", "perdido", "descartado",
];
const PRIORIDADES = ["alta", "media", "baja"];

const EMPTY = {
  nombre: "", sitioWeb: "", email: "", telefono: "", ciudadZona: "", nEdificios: "",
  estado: "sin_contactar", prioridad: "media", asignadoA: "", asignadoEmail: "",
  contactoNombre: "", contactoTel: "", contactoEmail: "",
  proximaAccion: "", proximaAccionFecha: "",
};

// Los campos que se editan en este formulario. El resto del documento (notas,
// reloj de asignación, historial, cotizaciones Back office, datos del
// relevamiento) no se reescribe desde acá.
const CAMPOS_EDITABLES = Object.keys(EMPTY);
const valorCampo = (v) => (v === undefined || v === null ? "" : String(v));

/** Campos editables que cambiaron entre `base` (la ficha al abrirla) y `form`. */
function camposCambiados(base, form) {
  return CAMPOS_EDITABLES.filter((k) => valorCampo(form[k]) !== valorCampo(base[k]));
}

/**
 * Ficha CRM de una ADMINISTRACIÓN de edificios (colección `administraciones`, sin
 * mapa). Igual que DesarrolladoraFicha: datos de la empresa + CRM con bloqueo de
 * 60 días. Si viene de un relevamiento (Google Maps) muestra rating/reseñas como
 * contexto de solo lectura. Muestra también la última cotización Back office
 * (solo lectura) y, para el superadmin, el acceso "Cotizar Back office".
 */
export default function AdministracionFicha({ open, onClose, admin, onSaved, comerciales = [], setSelected }) {
  const { showToast } = useToast();
  const { confirmDialog } = useAppDialog();
  const isMobile = useIsMobile();
  const { userType } = useUser();
  const { setPrefill } = useCotizadorPrefill();
  const email = auth.currentUser?.email || "comercial";
  const emailLc = String(email).toLowerCase();
  const isSuperAdmin = Number(userType) === 2;
  const miNombre = comerciales.find((c) => c.email === emailLc)?.name || email;

  const [form, setForm] = useState(EMPTY);
  const [notas, setNotas] = useState([]);
  const [nuevaNota, setNuevaNota] = useState("");
  const [saving, setSaving] = useState(false);
  // La ficha como estaba al abrirla: al guardar se escriben solo los campos que
  // cambiaron respecto de esto, y "Cotizar Back office" la usa para saber si
  // hay cambios sin guardar.
  const baseRef = useRef(EMPTY);

  useEffect(() => {
    if (!open) return;
    const base = { ...EMPTY, ...(admin || {}) };
    if (!admin?.id && !isSuperAdmin) { base.asignadoEmail = emailLc; base.asignadoA = miNombre; }
    baseRef.current = base;
    setForm(base);
    setNotas(Array.isArray(admin?.notas) ? admin.notas : []);
    setNuevaNota("");
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [open, admin?.id]);

  const nowMs = Date.now();
  const esNueva = !admin?.id;
  const bloqueado = estaBloqueado(admin, nowMs);
  const titular = esTitular(admin, emailLc);
  const libre = estaLibre(admin, nowMs);
  const fechaLib = fmtFechaLiberacion(admin);
  const editable = esNueva || puedeEditar(admin, { email: emailLc, userType }, nowMs);

  const set = (k) => (e) => editable && setForm((f) => ({ ...f, [k]: e.target.value }));

  /** Guarda la ficha. Devuelve true si se guardó (y la ficha se cerró). */
  async function guardar() {
    if (!editable) { showToast("Esta administración está asignada a otro agente.", "error"); return false; }
    if (!form.nombre.trim()) { showToast("El nombre es obligatorio.", "error"); return false; }
    setSaving(true);
    let guardada = false;
    try {
      const nowTs = Timestamp.now();
      const notaNueva = nuevaNota.trim()
        ? { texto: nuevaNota.trim(), autor: email, at: new Date().toISOString() }
        : null;
      const notasLocal = notaNueva ? [...notas, notaNueva] : notas;

      // Campos de asignación que se recalculan en ESTE guardado (si no, no se mandan).
      const reloj = {};
      let entradaHistorial = null;
      const prevEmail = (admin?.asignadoEmail || "");
      const newEmail = (form.asignadoEmail || "");
      const asignacionCambio = newEmail !== prevEmail;
      const historialPrev = Array.isArray(admin?.asignacionHistorial) ? admin.asignacionHistorial : [];

      if (asignacionCambio && newEmail) {
        const nombre = comerciales.find((c) => c.email === newEmail)?.name || form.asignadoA || newEmail;
        const tipo = isSuperAdmin && prevEmail && estaBloqueado(admin, nowTs.toMillis()) ? "reasignacion_superadmin" : "asignacion";
        reloj.asignadoEmail = newEmail;
        reloj.asignadoA = nombre;
        reloj.asignadoAt = nowTs;
        reloj.ultimaGestionAt = nowTs;
        reloj.bloqueadoHasta = computeBloqueadoHasta(nowTs.toDate());
        entradaHistorial = { email: newEmail, name: nombre, at: new Date().toISOString(), por: email, tipo };
      } else if (asignacionCambio && !newEmail) {
        reloj.asignadoAt = null;
        reloj.bloqueadoHasta = null;
        reloj.liberadoAt = nowTs;
        entradaHistorial = { email: "", name: "", at: new Date().toISOString(), por: email, tipo: "liberacion_manual" };
      } else if (newEmail && esGestion(admin, form, nuevaNota)) {
        reloj.ultimaGestionAt = nowTs;
        reloj.bloqueadoHasta = computeBloqueadoHasta(nowTs.toDate());
      }

      let id = admin?.id;
      let local; // lo que se refleja en la lista en memoria (sin valores especiales de Firestore)
      if (!id) {
        // Alta: el documento no existe todavía, se escribe completo.
        const datos = { ...form, ...reloj, notas: notasLocal, updatedBy: email, origen: "manual" };
        if (entradaHistorial) datos.asignacionHistorial = [entradaHistorial];
        const ref = await addDoc(collection(db, "administraciones"), {
          ...datos, createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
        });
        id = ref.id;
        local = { ...datos, createdAt: nowTs, updatedAt: nowTs };
      } else {
        // Edición: solo lo que cambió en esta ficha, más el reloj si se recalculó.
        // Así una lista en memoria vieja no pisa lo que otro guardó mientras tanto
        // (notas, el estado y el bloqueo que deja el Cotizador Back office, el
        // historial de asignación): la nota y el historial se suman con arrayUnion.
        const cambios = {};
        camposCambiados(baseRef.current, form).forEach((k) => { cambios[k] = valorCampo(form[k]); });
        const payload = { ...cambios, ...reloj, updatedAt: serverTimestamp(), updatedBy: email };
        if (notaNueva) payload.notas = arrayUnion(notaNueva);
        if (entradaHistorial) payload.asignacionHistorial = arrayUnion(entradaHistorial);

        const ref = doc(db, "administraciones", id);
        const esReclamoComercial = !isSuperAdmin && asignacionCambio && !!newEmail;
        if (esReclamoComercial) {
          await runTransaction(db, async (tx) => {
            const snap = await tx.get(ref);
            const cur = snap.exists() ? snap.data() : {};
            if (!estaLibre(cur, Date.now())) throw new Error("OCUPADO");
            tx.set(ref, payload, { merge: true });
          });
        } else {
          await setDoc(ref, payload, { merge: true });
        }
        local = { ...cambios, ...reloj, notas: notasLocal, updatedAt: nowTs, updatedBy: email };
        if (entradaHistorial) local.asignacionHistorial = [...historialPrev, entradaHistorial];
      }

      showToast(esNueva ? "Administración creada." : "Ficha guardada.", "success");
      onSaved && onSaved(id, local);
      onClose && onClose();
      guardada = true;
    } catch (e) {
      if (e?.message === "OCUPADO") showToast("Otro agente la tomó recién. Refrescá para ver el estado.", "error");
      else showToast(e.message || "No se pudo guardar.", "error");
    }
    setSaving(false);
    return guardada;
  }

  const sitioUrl = form.sitioWeb
    ? (/^https?:\/\//.test(form.sitioWeb) ? form.sitioWeb : `https://${form.sitioWeb}`)
    : null;
  const notasOrden = [...notas].reverse();

  // Cotización Back office: la última, en solo lectura; cotizar es del superadmin.
  const ultimaCotBO = admin?.cotizacionBackoffice && typeof admin.cotizacionBackoffice === "object"
    ? admin.cotizacionBackoffice
    : null;
  const puedeCotizarBO = isSuperAdmin && !esNueva && typeof setSelected === "function";

  // Abre el Cotizador Back office con esta administración vinculada (lo consume
  // al montar). Si hay cambios sin guardar en la ficha, antes pregunta: ir al
  // cotizador cierra la ficha y los perdería.
  async function irACotizarBackoffice() {
    const hayCambios = !!nuevaNota.trim() || camposCambiados(baseRef.current, form).length > 0;
    if (hayCambios) {
      const guardarAntes = await confirmDialog(
        "Tenés cambios sin guardar en la ficha: ¿los guardás antes de cotizar? Si no, se pierden al ir al cotizador.",
        { title: "Cambios sin guardar", confirmLabel: "Guardar y cotizar", cancelLabel: "Seguir editando" }
      );
      if (!guardarAntes) return;
      // guardar() cierra la ficha si sale bien; si falla, se queda abierta con el aviso.
      if (!(await guardar())) return;
    }
    setPrefill({ origen: "administracion", administracionId: admin.id, nombre: form.nombre.trim() || admin.nombre || "" });
    if (!hayCambios && onClose) onClose();
    setSelected("cotizadorbackoffice");
  }

  const body = (
    <>
      {!editable && (
        <div style={roBanner}>
          <FaLock size={12} /> Asignada a {admin?.asignadoA || "otro agente"} · se libera el {fechaLib || "—"}. Solo lectura hasta entonces.
        </div>
      )}

      {/* Contexto del relevamiento (Google Maps), si lo hay */}
      {(admin?.rating != null || admin?.nReviews != null) && (
        <div style={ctxBox}>
          {admin?.rating != null && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color: colors.warningDark }}>
              <FaStar size={12} /> {admin.rating} {admin?.nReviews != null && <span style={{ color: colors.gray600 }}>· {admin.nReviews} reseñas en Google</span>}
            </span>
          )}
        </div>
      )}

      {/* Datos de la administración */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: spacing[4] }}>
        <FormField label="Nombre *" htmlFor="adm-nombre" fullWidth>
          <input id="adm-nombre" value={form.nombre} onChange={set("nombre")} disabled={!editable} placeholder="Nombre de la administración" style={{ ...inputStyle, ...(editable ? {} : inputRO) }} />
        </FormField>
        <FormField label="Sitio web" htmlFor="adm-web">
          <input id="adm-web" value={form.sitioWeb} onChange={set("sitioWeb")} disabled={!editable} placeholder="ejemplo.com.uy" style={{ ...inputStyle, ...(editable ? {} : inputRO) }} />
          {sitioUrl && (
            <a href={sitioUrl} target="_blank" rel="noreferrer" style={webLink}>Abrir sitio <FaExternalLinkAlt size={9} /></a>
          )}
        </FormField>
        <FormField label="Ciudad / Zona" htmlFor="adm-zona">
          <input id="adm-zona" value={form.ciudadZona} onChange={set("ciudadZona")} disabled={!editable} placeholder="Montevideo (Pocitos)" style={{ ...inputStyle, ...(editable ? {} : inputRO) }} />
        </FormField>
        <FormField label="Email" htmlFor="adm-email">
          <input id="adm-email" type="email" value={form.email} onChange={set("email")} disabled={!editable} placeholder="contacto@empresa.uy" style={{ ...inputStyle, ...(editable ? {} : inputRO) }} />
        </FormField>
        <FormField label="Teléfono" htmlFor="adm-tel">
          <input id="adm-tel" value={form.telefono} onChange={set("telefono")} disabled={!editable} placeholder="+598 …" style={{ ...inputStyle, ...(editable ? {} : inputRO) }} />
        </FormField>
        <FormField label="Edificios (indicio)" htmlFor="adm-edif" fullWidth>
          <input id="adm-edif" value={form.nEdificios} onChange={set("nEdificios")} disabled={!editable} placeholder="Ej. ~40 edificios" style={{ ...inputStyle, ...(editable ? {} : inputRO) }} />
        </FormField>
      </div>

      {/* CRM */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: spacing[4], marginTop: spacing[5] }}>
        <FormField label="Estado">
          <AppSelect value={form.estado} ariaLabel="Estado" disabled={!editable}
            onChange={(v) => editable && setForm((f) => ({ ...f, estado: v }))}
            options={CRM_ESTADOS.map((e) => ({ value: e, label: STATUS_LABELS[e] || e }))} />
        </FormField>
        <FormField label="Prioridad">
          <AppSelect value={form.prioridad} ariaLabel="Prioridad" disabled={!editable}
            onChange={(v) => editable && setForm((f) => ({ ...f, prioridad: v }))}
            options={PRIORIDADES.map((e) => ({ value: e, label: STATUS_LABELS[e] || e }))} />
        </FormField>
        <FormField label="Asignado a">
          {isSuperAdmin ? (
            <>
              <AppSelect
                value={form.asignadoEmail || ""}
                ariaLabel="Asignado a"
                onChange={(em) => {
                  const c = comerciales.find((x) => x.email === em);
                  setForm((f) => ({ ...f, asignadoEmail: em, asignadoA: c ? c.name : "" }));
                }}
                options={[{ value: "", label: "— Sin asignar —" }, ...comerciales.map((c) => ({ value: c.email, label: c.name || c.email }))]}
              />
              {bloqueado && (
                <div style={lockNote}>
                  <FaLock size={10} /> Bloqueada para {admin?.asignadoA || "el agente"} hasta {fechaLib}. Reasignar queda registrado como override.
                </div>
              )}
            </>
          ) : titular ? (
            <div style={chipOk}><FaCheck size={11} /> Asignada a vos{bloqueado ? ` · se libera el ${fechaLib}` : ""}</div>
          ) : libre ? (
            form.asignadoEmail === emailLc ? (
              <div style={chipOk}><FaCheck size={11} /> Te la asignás al guardar</div>
            ) : (
              <button type="button" onClick={() => setForm((f) => ({ ...f, asignadoEmail: emailLc, asignadoA: miNombre }))} style={tomarBtn}>
                <FaHandPointer size={12} /> Tomar esta administración
              </button>
            )
          ) : (
            <div style={chipLock}><FaLock size={11} /> Asignada a {admin?.asignadoA || "otro agente"} · se libera el {fechaLib}</div>
          )}
        </FormField>

        <FormField label="Contacto — nombre" htmlFor="adm-cnom">
          <input id="adm-cnom" value={form.contactoNombre} onChange={set("contactoNombre")} disabled={!editable} placeholder="Ej. gerente / titular" style={{ ...inputStyle, ...(editable ? {} : inputRO) }} />
        </FormField>
        <FormField label="Contacto — teléfono" htmlFor="adm-ctel">
          <input id="adm-ctel" value={form.contactoTel} onChange={set("contactoTel")} disabled={!editable} placeholder="09x xxx xxx" style={{ ...inputStyle, ...(editable ? {} : inputRO) }} />
        </FormField>
        <FormField label="Contacto — email" htmlFor="adm-cmail">
          <input id="adm-cmail" type="email" value={form.contactoEmail} onChange={set("contactoEmail")} disabled={!editable} placeholder="mail@ejemplo.com" style={{ ...inputStyle, ...(editable ? {} : inputRO) }} />
        </FormField>

        <FormField label="Próxima acción" htmlFor="adm-prox" fullWidth>
          <input id="adm-prox" value={form.proximaAccion} onChange={set("proximaAccion")} disabled={!editable} placeholder="Ej. llamar, mandar propuesta…" style={{ ...inputStyle, ...(editable ? {} : inputRO) }} />
        </FormField>
        <FormField label="Fecha próxima acción" htmlFor="adm-proxf">
          <input id="adm-proxf" type="date" value={form.proximaAccionFecha || ""} onChange={set("proximaAccionFecha")} disabled={!editable} style={{ ...inputStyle, ...(editable ? {} : inputRO) }} />
        </FormField>
      </div>

      {/* Cotización Back office: última cotización (solo lectura) + acceso al cotizador */}
      {(ultimaCotBO || puedeCotizarBO) && (
        <div style={cotBox}>
          <FaFileInvoiceDollar size={14} color={colors.primary} style={{ flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 180, fontSize: typography.sizes.base, color: ultimaCotBO ? colors.gray700 : colors.gray600 }}>
            {ultimaCotBO
              ? `Última cotización Back office: ${[
                ultimaCotBO.nro,
                ultimaCotBO.planNombre || PLANES_BO[ultimaCotBO.plan]?.nombre,
                `${fmtU(ultimaCotBO.totalFinal)} ${IVA_LEYENDA}`,
                fechaCortaBO(ultimaCotBO.at),
              ].filter(Boolean).join(" · ")}`
              : "Todavía no tiene cotizaciones Back office."}
          </div>
          {puedeCotizarBO && (
            <button type="button" onClick={irACotizarBackoffice} style={cotBtn} className="habitia-btn-secondary">
              <FaFileInvoiceDollar size={11} /> Cotizar Back office
            </button>
          )}
        </div>
      )}

      {/* Armar mensaje de contacto (WhatsApp / mail) + material de planes */}
      {form.nombre.trim() && (
        <div style={{ marginTop: spacing[5] }}>
          <div style={{ display: "flex", alignItems: "center", gap: spacing[2], marginBottom: spacing[2] }}>
            <FaPaperPlane size={12} color={colors.gray400} />
            <span style={{ fontSize: typography.sizes.base, color: colors.gray700 }}>Armar mensaje de contacto</span>
          </div>
          <ContactoAdministracion admin={form} />
        </div>
      )}

      {/* Notas */}
      <div style={{ marginTop: spacing[5] }}>
        <div style={{ display: "flex", alignItems: "center", gap: spacing[2], marginBottom: spacing[2] }}>
          <FaRegStickyNote size={13} color={colors.gray400} />
          <span style={{ fontSize: typography.sizes.base, color: colors.gray700 }}>Notas de gestión</span>
        </div>
        <textarea
          value={nuevaNota}
          onChange={(e) => editable && setNuevaNota(e.target.value)}
          disabled={!editable}
          placeholder={editable ? "Agregar nota (queda con tu usuario y fecha)…" : "Tomá la administración para gestionarla."}
          rows={2}
          style={{ ...inputStyle, width: "100%", resize: "vertical", fontFamily: typography.fontFamily, ...(editable ? {} : inputRO) }}
        />
        {notasOrden.length > 0 && (
          <div style={{ marginTop: spacing[3], display: "grid", gap: spacing[2] }}>
            {notasOrden.map((n, i) => (
              <div key={i} style={notaItem}>
                <div style={{ fontSize: typography.sizes.base, color: colors.gray700, whiteSpace: "pre-wrap" }}>{n.texto}</div>
                <div style={{ fontSize: typography.sizes.xs, color: colors.gray400, marginTop: 2 }}>
                  {n.autor} · {n.at ? new Date(n.at).toLocaleString("es-UY") : ""}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );

  if (!open) return null;

  if (isMobile) {
    return (
      <div style={fsWrap}>
        <div style={fsHeader}>
          <button onClick={onClose} style={backBtn} aria-label="Volver"><FaArrowLeft size={13} /> Volver</button>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: typography.sizes.base, color: colors.gray900, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {form.nombre || (esNueva ? "Nueva administración" : "Administración")}
            </div>
          </div>
        </div>
        <div style={fsBody}>{body}</div>
        <div style={fsFooter}>
          <button onClick={onClose} style={{ ...btnGhost, flex: 1, justifyContent: "center" }}>Cancelar</button>
          <button onClick={guardar} disabled={saving || !editable} style={{ ...btnPrimary, flex: 1, justifyContent: "center" }}>
            <FaCheck size={12} /> {saving ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <AdminModal
      open={open}
      onClose={onClose}
      size="wide"
      title={esNueva ? "Nueva administración" : `Administración · ${admin?.nombre || ""}`}
      icon={<FaBuilding size={18} />}
      footer={
        <>
          <button onClick={onClose} style={btnGhost}>Cancelar</button>
          <button onClick={guardar} disabled={saving || !editable} style={btnPrimary}>
            <FaCheck size={12} /> {saving ? "Guardando…" : esNueva ? "Crear" : "Guardar ficha"}
          </button>
        </>
      }
    >
      {body}
    </AdminModal>
  );
}

const inputStyle = {
  width: "100%", padding: "11px 12px", minHeight: 44,
  border: `1px solid ${colors.gray200}`, borderRadius: radius.sm,
  fontSize: typography.sizes.base, fontFamily: typography.fontFamily,
  background: colors.white, color: colors.gray900, boxSizing: "border-box",
};
const inputRO = { background: colors.gray50, color: colors.gray600, cursor: "not-allowed" };
const ctxBox = { padding: spacing[3], background: colors.gray50, borderRadius: radius.md, marginBottom: spacing[4], fontSize: typography.sizes.sm };
const roBanner = { display: "flex", alignItems: "center", gap: 6, marginBottom: spacing[4], padding: "10px 12px", borderRadius: radius.sm, background: colors.gray100, color: colors.gray700, fontSize: typography.sizes.sm };
const lockNote = { display: "flex", alignItems: "center", gap: 5, marginTop: 6, fontSize: typography.sizes.xs, color: colors.warningDark };
const chipOk = { display: "inline-flex", alignItems: "center", gap: 6, padding: "10px 12px", minHeight: 42, boxSizing: "border-box", borderRadius: radius.sm, background: colors.successLight, color: colors.successDark, fontSize: typography.sizes.sm };
const chipLock = { display: "inline-flex", alignItems: "center", gap: 6, padding: "10px 12px", minHeight: 42, boxSizing: "border-box", borderRadius: radius.sm, background: colors.gray100, color: colors.gray700, fontSize: typography.sizes.sm };
const tomarBtn = { display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, width: "100%", padding: "10px 12px", minHeight: 42, boxSizing: "border-box", borderRadius: radius.sm, border: `1px solid ${colors.primary}`, background: colors.primaryLighter, color: colors.primary, cursor: "pointer", fontSize: typography.sizes.base, fontFamily: "inherit" };
const webLink = { display: "inline-flex", alignItems: "center", gap: 5, marginTop: 5, fontSize: typography.sizes.sm, color: colors.primary, textDecoration: "none" };
const notaItem = { padding: spacing[3], background: colors.white, border: `1px solid ${colors.gray200}`, borderRadius: radius.sm };
const cotBox = { display: "flex", alignItems: "center", gap: spacing[3], flexWrap: "wrap", marginTop: spacing[5], padding: spacing[3], background: colors.primarySubtle, borderRadius: radius.md };
const cotBtn = { ...btnSecondary, padding: `${spacing[2]}px ${spacing[4]}px`, fontSize: typography.sizes.base, whiteSpace: "nowrap" };
const fsWrap = { position: "fixed", inset: 0, zIndex: 2000, background: colors.white, display: "flex", flexDirection: "column" };
const fsHeader = { display: "flex", alignItems: "center", gap: spacing[3], padding: `${spacing[3]}px ${spacing[4]}px`, borderBottom: `1px solid ${colors.gray200}`, flexShrink: 0 };
const backBtn = { display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 12px", background: colors.primaryLighter, color: colors.primary, border: "none", borderRadius: radius.sm, cursor: "pointer", fontSize: typography.sizes.base, fontFamily: "inherit", flexShrink: 0 };
const fsBody = { flex: 1, minHeight: 0, overflowY: "auto", WebkitOverflowScrolling: "touch", padding: spacing[4] };
const fsFooter = { display: "flex", gap: spacing[2], padding: spacing[3], paddingBottom: "calc(12px + env(safe-area-inset-bottom, 0px))", borderTop: `1px solid ${colors.gray200}`, background: colors.white, flexShrink: 0 };
const btnGhost = { padding: `${spacing[2]}px ${spacing[4]}px`, background: colors.white, border: `1px solid ${colors.gray200}`, borderRadius: radius.sm, cursor: "pointer", fontSize: typography.sizes.base, color: colors.gray700 };
const btnPrimary = { display: "inline-flex", alignItems: "center", gap: spacing[1], padding: `${spacing[2]}px ${spacing[4]}px`, background: colors.primary, color: colors.white, border: "none", borderRadius: radius.sm, cursor: "pointer", fontSize: typography.sizes.base };
